import api, { handleApiError } from "./api";
import { AxiosError } from "axios";

export type ContractKind =
  | "main_contract"
  | "disclosure"
  | "addendum"
  | "ach_addendum"
  | "refinance_contract";

export const CONTRACT_KIND_LABELS: Record<ContractKind, string> = {
  main_contract: "Main Contract",
  disclosure: "Pre-disclosure",
  addendum: "Addendum",
  ach_addendum: "ACH Addendum",
  refinance_contract: "Refinance Contract",
};

export const CONTRACT_KINDS = Object.keys(CONTRACT_KIND_LABELS) as ContractKind[];

/** Mirrors the backend's `ContractDocument.JURISDICTION_KINDS`. */
const JURISDICTION_KINDS: ReadonlySet<ContractKind> = new Set(["disclosure"]);

/** Whether a kind's lane is split by jurisdiction. Every other kind is the null lane. */
export function kindHasJurisdiction(kind: ContractKind): boolean {
  return JURISDICTION_KINDS.has(kind);
}

export interface ContractDocument {
  id: number;
  kind: ContractKind;
  name: string;
  docuseal_template_id: number;
  /** Catalog code, or null for the general disclosure lane. */
  jurisdiction: string | null;
  is_default: boolean;
  is_active: boolean;
  /** Shared by every organization and managed by system admins; read-only to an org. */
  is_system: boolean;
  field_map?: Record<string, string>;
  created_at: string;
}

export interface FieldValidation {
  checked: boolean;
  missing: string[];
  available: string[];
  error?: string;
}

export interface ContractDocumentWithValidation extends ContractDocument {
  field_validation?: FieldValidation;
}

export interface FormDataField {
  field_name: string;
  label: string;
  value: string;
}

export interface FormDataRecipient {
  name: string;
  /** Address the invite is actually sent to (trial user's own email for trial orgs). */
  email: string;
  /** Merchant email as captured on the application. */
  captured_email?: string;
  is_trial_override?: boolean;
}

export interface ContractFormData {
  document: { id: number; name: string; kind: ContractKind };
  fields: FormDataField[];
  recipient: FormDataRecipient;
}

export type SubmissionStatus =
  | "pending"
  | "sent"
  | "delivered"
  | "signed"
  | "completed"
  | "declined"
  | "voided"
  | "failed";

export interface ContractSubmission {
  id: number;
  external_id: string;
  document_name: string;
  contract_document: number | null;
  contract_kind: ContractKind;
  /** Catalog code, or null for the general lane. */
  jurisdiction: string | null;
  invalidated_at: string | null;
  status: SubmissionStatus;
  failure_reason: string;
  recipient_email: string;
  recipient_name: string;
  sent_at: string | null;
  completed_at: string | null;
  expire_at: string | null;
  resent_from: number | null;
  has_signed_document: boolean;
  created_at: string;
}

export interface SubmissionStatusResponse {
  current: ContractSubmission | null;
  /** Every sent/delivered row in the lane, including `current` when it is live. */
  live: ContractSubmission[];
  history: ContractSubmission[];
}

export interface JurisdictionOption {
  code: string;
  name: string;
}

export interface JurisdictionCatalog {
  jurisdictions: JurisdictionOption[];
  requiring: string[];
}

/** Code → name and uppercase name → code, built from the backend catalog. */
export function buildJurisdictionMaps(jurisdictions: JurisdictionOption[]) {
  const byCode: Record<string, string> = {};
  const byName: Record<string, string> = {};
  for (const row of jurisdictions) {
    byCode[row.code] = row.name;
    byName[row.name.toUpperCase()] = row.code;
  }
  return { byCode, byName };
}

export interface RegisterContractDocumentRequest {
  kind: ContractKind;
  name: string;
  docuseal_template_id: number;
  is_default?: boolean;
  /** Null is the general disclosure. Omit on kinds that are not pre-disclosure. */
  jurisdiction?: string | null;
  field_map?: Record<string, string>;
  /** Also remove variables left unplaced in the template's field list (Values offers them). */
  clean_template_fields?: boolean;
  /** false saves the row turned off; it is then never the default. Defaults to on. */
  is_active?: boolean;
}

/** One variable a kind seeds into the builder's Values tab. */
export interface FieldSeed {
  field_name: string;
  label: string;
  docuseal_field_name: string;
}

export type FieldSeedsByKind = Record<ContractKind, FieldSeed[]>;

/** A DocuSeal template as the editor reads it. */
export interface RemoteTemplate {
  id: number;
  name: string;
  /** For a template the merchant only fills: how it would become a Document instead. */
  document_hint?: string;
}

export interface BuilderTokenResponse {
  token: string;
  host: string;
  max_pdf_bytes: number;
  /** Editing only: the template's party names, after its Funder party was added. */
  roles?: string[];
}

export interface UpdateContractDocumentRequest {
  name?: string;
  /** A change re-seeds the template's variables and moves the document to that lane. */
  kind?: ContractKind;
  is_default?: boolean;
  /** false turns the document off but keeps its row, so its type is remembered. */
  is_active?: boolean;
  jurisdiction?: string | null;
  field_map?: Record<string, string>;
}

/** Code returned by the send endpoint on a pre-reservation failure (no row persisted). */
export type SendErrorCode = "transient" | "missing_required" | "validation" | "unknown";

export interface SendErrorInfo {
  message: string;
  code: SendErrorCode;
  retryable: boolean;
}

/** Contract-document and template management under `prefix`, which every scope mirrors. */
function adminDocumentService(prefix: string) {
  return {
    listAdminDocuments: async (): Promise<ContractDocument[]> => {
      const { data } = await api.get<ContractDocument[]>(`${prefix}/documents/`);
      return data;
    },

    registerDocument: async (
      payload: RegisterContractDocumentRequest
    ): Promise<ContractDocumentWithValidation> => {
      const { data } = await api.post<ContractDocumentWithValidation>(
        `${prefix}/documents/`,
        payload
      );
      return data;
    },

    updateDocument: async (
      docId: number,
      payload: UpdateContractDocumentRequest
    ): Promise<ContractDocumentWithValidation> => {
      const { data } = await api.patch<ContractDocumentWithValidation>(
        `${prefix}/documents/${docId}/`,
        payload
      );
      return data;
    },

    // ── Template authoring (embedded builder) ──
    listFieldSeeds: async (): Promise<FieldSeedsByKind> => {
      const { data } = await api.get<FieldSeedsByKind>(`${prefix}/documents/field-seeds/`);
      return data;
    },

    /** Edit an owned template (`templateId`), or start a new one named `name`. */
    mintBuilderToken: async (target: {
      templateId?: number;
      name?: string;
    }): Promise<BuilderTokenResponse> => {
      const { data } = await api.post<BuilderTokenResponse>(`${prefix}/builder-token/`, {
        ...(target.templateId != null ? { template_id: target.templateId } : { name: target.name }),
      });
      return data;
    },

    getRemoteTemplate: async (templateId: number): Promise<RemoteTemplate> => {
      const { data } = await api.get<RemoteTemplate>(`${prefix}/templates/${templateId}/`);
      return data;
    },

    /** Saves the template with no fields (the builder will not); it is sent as its raw PDF. */
    clearTemplateFields: async (templateId: number): Promise<void> => {
      await api.post(`${prefix}/templates/${templateId}/clear-fields/`);
    },

    deleteRemoteTemplate: async (templateId: number): Promise<void> => {
      await api.delete(`${prefix}/templates/${templateId}/`);
    },
  };
}

export type AdminDocumentService = ReturnType<typeof adminDocumentService>;

export const docusealService = {
  /** Exactly one of assessmentId/refinanceOfferId should be set, matching the selected document's kind. */
  getFormData: async (
    borrowerId: number,
    documentId: number,
    opts: { assessmentId?: number; refinanceOfferId?: number }
  ): Promise<ContractFormData> => {
    const { data } = await api.get<ContractFormData>(`/docuseal/form-data/${borrowerId}/`, {
      params: {
        document_id: documentId,
        assessment_id: opts.assessmentId,
        refinance_offer_id: opts.refinanceOfferId,
      },
    });
    return data;
  },

  /** Status/history are scoped to one lane: kind plus jurisdiction (null = general). */
  getStatus: async (
    borrowerId: number,
    kind: ContractKind,
    jurisdiction?: string | null
  ): Promise<SubmissionStatusResponse> => {
    const params: { kind: ContractKind; jurisdiction?: string } = { kind };
    if (jurisdiction) params.jurisdiction = jurisdiction;
    const { data } = await api.get<SubmissionStatusResponse>(`/docuseal/status/${borrowerId}/`, {
      params,
    });
    return data;
  },

  listJurisdictions: async (): Promise<JurisdictionCatalog> => {
    const { data } = await api.get<JurisdictionCatalog>(`/core/jurisdictions/`);
    return data;
  },

  /** Re-send the invite email for the EXISTING active submission (same signing link). */
  resendInviteEmail: async (externalId: string): Promise<{ detail: string }> => {
    const { data } = await api.post<{ detail: string }>(
      `/docuseal/submissions/${externalId}/resend-email/`
    );
    return data;
  },

  downloadSignedDocument: async (externalId: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`/docuseal/submissions/${externalId}/signed-document/`, {
      responseType: "blob",
    });
    const disposition = (response.headers?.["content-disposition"] as string) ?? "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] ?? `contract-${externalId}.pdf`;
    return { blob: response.data as Blob, filename };
  },

  downloadTemplateDocument: async (
    documentId: number
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`/docuseal/documents/${documentId}/template-document/`, {
      responseType: "blob",
    });
    const disposition = (response.headers?.["content-disposition"] as string) ?? "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] ?? `contract-${documentId}.pdf`;
    return { blob: response.data as Blob, filename };
  },

  ...adminDocumentService("/docuseal/admin"),
};

/** Where a scope's template admin pages live and which endpoints back them. */
export interface DocumentScope {
  /** Query and React keys only. */
  key: "org" | "system";
  title: string;
  basePath: string;
  service: AdminDocumentService;
  /** It manages the system templates. Any other scope lists them beside its own, read-only. */
  isSystem: boolean;
  /** Its sends fail without a default in each required lane, so the list warns about gaps. */
  requiresDefaults: boolean;
}

/** An org's own templates, and the system templates every org shares. */
export const DOCUMENT_SCOPES = {
  org: {
    key: "org",
    title: "Templates",
    basePath: "/documents",
    service: docusealService,
    isSystem: false,
    requiresDefaults: true,
  },
  system: {
    key: "system",
    title: "System templates",
    basePath: "/system/documents",
    service: adminDocumentService("/docuseal/system"),
    isSystem: true,
    requiresDefaults: false,
  },
} as const satisfies Record<DocumentScope["key"], DocumentScope>;

export const getDocusealErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    return handleApiError(error);
  }
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred";
};

export const isForbiddenError = (error: unknown): boolean =>
  error instanceof AxiosError && error.response?.status === 403;

/** Parse a send error into a typed shape so the UI can distinguish retry vs fix-template. */
export const parseSendError = (error: unknown): SendErrorInfo => {
  if (error instanceof AxiosError) {
    const data = (error.response?.data ?? {}) as {
      detail?: string;
      code?: SendErrorCode;
      retryable?: boolean;
    };
    const httpStatus = error.response?.status;
    const code: SendErrorCode = data.code ?? (httpStatus === 400 ? "validation" : "unknown");
    return {
      message: handleApiError(error),
      code,
      retryable: data.retryable ?? code === "transient",
    };
  }
  return { message: getDocusealErrorMessage(error), code: "unknown", retryable: false };
};
